// @ts-nocheck
import { supabase } from '@/lib/supabaseGuard';
import EnhancedChatCache from '@/services/cache/EnhancedChatCache';
import { logger } from '../../utils/productionLogger';
import { rpcChatUsersForViewer } from '@/lib/d001PeerUsersRpc';

// Database-only queries - no fallback data

// 🚀 CHAT CACHE: USER-SPECIFIC localStorage-based caching for conversations
interface ChatCacheItem {
  data: ChatConversation[];
  timestamp: number;
  expiry: number;
  userId: string; // 🔒 SECURITY: Track which user this cache belongs to
}

// 🚀 AUGMENT NOTIFICATION CACHE: Extend cache system for instant notification counts
interface NotificationCacheItem {
  chatUnreadCounts: Record<string, number>;
  totalUnreadCount: number;
  timestamp: number;
  expiry: number;
  userId: string;
}

const CHAT_CACHE_PREFIX = 'blissnet_chat_conversations_';
const CHAT_CACHE_TTL = 5 * 60 * 1000; // 5 minutes

// 🚀 AUGMENT NOTIFICATION CACHE: Constants for notification count caching
const NOTIFICATION_CACHE_PREFIX = 'blissnet_notification_counts_';
const NOTIFICATION_CACHE_TTL = 2 * 60 * 1000; // 2 minutes (shorter TTL for accuracy)

// 🚀 AUGMENT INSTANT CACHE: In-memory cache for instant access
class InstantChatCache {
  private static memoryCache = new Map<string, ChatConversation[]>();

  static set(userId: string, data: ChatConversation[]): void {
    if (!userId) return;
    this.memoryCache.set(userId, data);
    logger.debug(`🚀 AUGMENT: Instant cache updated for user ${userId} - ${data.length} conversations`);
  }

  static get(userId: string): ChatConversation[] | null {
    if (!userId) return null;
    const cached = this.memoryCache.get(userId);
    return cached || null;
  }

  static clear(userId: string): void {
    if (!userId) return;
    this.memoryCache.delete(userId);
  }

  static clearAll(): void {
    this.memoryCache.clear();
  }
}

class ChatCache {
  // 🔒 SECURITY: Generate user-specific cache key
  private static getCacheKey(userId: string): string {
    if (!userId) {
      logger.error('Cannot generate cache key without user ID');
      return '';
    }
    return `${CHAT_CACHE_PREFIX}${userId}`;
  }

  static set(data: ChatConversation[], userId: string): void {
    if (!userId) {
      logger.error('Cannot cache without user ID');
      return;
    }

    // 🚀 AUGMENT: Set both memory and localStorage cache
    InstantChatCache.set(userId, data);

    const cacheKey = this.getCacheKey(userId);
    if (!cacheKey) return;

    const cacheItem: ChatCacheItem = {
      data,
      timestamp: Date.now(),
      expiry: Date.now() + CHAT_CACHE_TTL,
      userId // 🔒 SECURITY: Store user ID for validation
    };
    try {
      localStorage.setItem(cacheKey, JSON.stringify(cacheItem));
    } catch (error) {
      logger.warn('Failed to cache chat data:', error);
    }
  }

  static get(userId: string): ChatConversation[] | null {
    if (!userId) {
      logger.error('Cannot retrieve cache without user ID');
      return null;
    }

    // 🚀 AUGMENT: Try memory cache first for instant access
    const memoryData = InstantChatCache.get(userId);
    if (memoryData !== null) {
      return memoryData;
    }

    // Fallback to localStorage cache
    const cacheKey = this.getCacheKey(userId);
    if (!cacheKey) return null;

    try {
      const cached = localStorage.getItem(cacheKey);
      if (!cached) return null;

      const cacheItem: ChatCacheItem = JSON.parse(cached);

      // 🔒 SECURITY: Validate cache belongs to current user
      if (cacheItem.userId !== userId) {
        logger.warn(`🚨 CHAT-CACHE: Cache user mismatch! Expected: ${userId}, Found: ${cacheItem.userId}`);
        localStorage.removeItem(cacheKey);
        return null;
      }

      // Check if cache is expired
      if (Date.now() > cacheItem.expiry) {
        localStorage.removeItem(cacheKey);
        return null;
      }

      // Populate memory cache for next time
      InstantChatCache.set(userId, cacheItem.data);

      return cacheItem.data;
    } catch (error) {
      logger.warn('Failed to read chat cache:', error);
      localStorage.removeItem(cacheKey);
      return null;
    }
  }

  static clear(userId: string): void {
    if (!userId) {
      logger.error('Cannot clear cache without user ID');
      return;
    }

    const cacheKey = this.getCacheKey(userId);
    if (cacheKey) {
      localStorage.removeItem(cacheKey);
      logger.debug(`🗑️ CHAT-CACHE: Cleared cache for user ${userId}`);
    }
  }

  // 🧹 SECURITY: Clear ALL chat caches (for logout/cleanup)
  static clearAll(): void {
    const keys = Object.keys(localStorage);
    const chatCacheKeys = keys.filter(key => key.startsWith(CHAT_CACHE_PREFIX));

    chatCacheKeys.forEach(key => {
      localStorage.removeItem(key);
    });

    logger.debug(`🗑️ CHAT-CACHE: Cleared ${chatCacheKeys.length} user caches`);
  }

  static isExpired(userId: string): boolean {
    if (!userId) return true;

    const cacheKey = this.getCacheKey(userId);
    if (!cacheKey) return true;

    try {
      const cached = localStorage.getItem(cacheKey);
      if (!cached) return true;

      const cacheItem: ChatCacheItem = JSON.parse(cached);

      // 🔒 SECURITY: Validate user
      if (cacheItem.userId !== userId) {
        localStorage.removeItem(cacheKey);
        return true;
      }

      return Date.now() > cacheItem.expiry;
    } catch {
      return true;
    }
  }
}

// 🚀 AUGMENT NOTIFICATION CACHE: Instant notification count caching system
class NotificationCache {
  // 🔒 SECURITY: Generate user-specific notification cache key
  private static getCacheKey(userId: string): string {
    if (!userId) {
      logger.error('Cannot generate notification cache key without user ID');
      return '';
    }
    return `${NOTIFICATION_CACHE_PREFIX}${userId}`;
  }

  static set(chatUnreadCounts: Record<string, number>, totalUnreadCount: number, userId: string): void {
    if (!userId) {
      logger.error('Cannot cache notifications without user ID');
      return;
    }

    const cacheKey = this.getCacheKey(userId);
    if (!cacheKey) return;

    const cacheItem: NotificationCacheItem = {
      chatUnreadCounts,
      totalUnreadCount,
      timestamp: Date.now(),
      expiry: Date.now() + NOTIFICATION_CACHE_TTL,
      userId
    };

    try {
      localStorage.setItem(cacheKey, JSON.stringify(cacheItem));
      logger.debug(`🔔 NOTIFICATION-CACHE: Cached counts for user ${userId} - Total: ${totalUnreadCount}`);
    } catch (error) {
      logger.warn('Failed to cache notification data:', error);
    }
  }

  static get(userId: string): { chatUnreadCounts: Record<string, number>; totalUnreadCount: number } | null {
    if (!userId) {
      logger.error('Cannot retrieve notification cache without user ID');
      return null;
    }

    const cacheKey = this.getCacheKey(userId);
    if (!cacheKey) return null;

    try {
      const cached = localStorage.getItem(cacheKey);
      if (!cached) return null;

      const cacheItem: NotificationCacheItem = JSON.parse(cached);

      // 🔒 SECURITY: Validate cache belongs to current user
      if (cacheItem.userId !== userId) {
        logger.warn(`🚨 NOTIFICATION-CACHE: Cache user mismatch! Expected: ${userId}, Found: ${cacheItem.userId}`);
        localStorage.removeItem(cacheKey);
        return null;
      }

      // Check if cache is expired
      if (Date.now() > cacheItem.expiry) {
        localStorage.removeItem(cacheKey);
        return null;
      }

      return {
        chatUnreadCounts: cacheItem.chatUnreadCounts || {},
        totalUnreadCount: cacheItem.totalUnreadCount || 0
      };
    } catch (error) {
      logger.warn('Failed to read notification cache:', error);
      localStorage.removeItem(cacheKey);
      return null;
    }
  }

  static isExpired(userId: string): boolean {
    if (!userId) return true;

    const cacheKey = this.getCacheKey(userId);
    if (!cacheKey) return true;

    try {
      const cached = localStorage.getItem(cacheKey);
      if (!cached) return true;

      const cacheItem: NotificationCacheItem = JSON.parse(cached);
      return Date.now() > cacheItem.expiry;
    } catch {
      return true;
    }
  }

  static clear(userId: string): void {
    if (!userId) return;

    const cacheKey = this.getCacheKey(userId);
    if (cacheKey) {
      localStorage.removeItem(cacheKey);
      logger.debug(`🗑️ NOTIFICATION-CACHE: Cleared cache for user ${userId}`);
    }
  }

  static clearAll(): void {
    const keys = Object.keys(localStorage);
    const notificationCacheKeys = keys.filter(key => key.startsWith(NOTIFICATION_CACHE_PREFIX));

    notificationCacheKeys.forEach(key => {
      localStorage.removeItem(key);
    });

    logger.debug(`🗑️ NOTIFICATION-CACHE: Cleared ${notificationCacheKeys.length} notification caches`);
  }
}

// Debug function to test connection access (dev only)
export async function debugConnectionAccess() {
  if (!isDev) return;

  try {
    const { data: { user } } = await supabase.auth.getUser();
    logger.debug('Current user:', user?.id, user?.email);

    const { data: connections, error } = await supabase
      .from('connections')
      .select('*');

    logger.debug('Accessible connections:', connections?.length || 0);
    if (error) logger.debug('Connection error:', error);

    if (connections) {
      connections.forEach(conn => {
        logger.debug(`Connection ${conn.id}: ${conn.user_id} ↔ ${conn.connection_user_id}`);
      });
    }
  } catch (error) {
    logger.error('Debug exception:', error);
  }
}

// Make debug function available in browser console for testing
if (typeof window !== 'undefined') {
  (window as any).debugConnectionAccess = debugConnectionAccess;
}

export interface ChatConversation {
  id: string;
  name: string;
  avatar?: string;
  photo?: string;
  lastMessage: string;
  timestamp: Date | string;
  unreadCount: number;
  isOnline?: boolean;
  isMatch?: boolean;
  isFriend?: boolean;
  isTyping?: boolean;
  otherUserId?: string; // ID of the other participant for avatar resolution
}

export interface ChatMessage {
  id: string;
  chat_id: string;
  sender_id: string;
  content: string;
  created_at: string;
  is_read: boolean;
  read?: boolean;
  sender?: {
    id: string;
    name: string;
    photo_urls?: string[];
  };
}

export interface ChatDetails {
  id: string;
  chat_type: 'match' | 'friend';
  connection_id?: string;
  match_id?: string;
  created_at: string;
  last_message_at?: string;
  unread_count: number;
  otherUser?: {
    id: string;
    name: string;
    photo_urls?: string[];
  };
}

export const chatService = {
  // Get chat by connection ID - support both new and legacy chat systems
  async getChatByConnection(connectionId: string): Promise<ChatDetails | null> {
    try {
      logger.debug('Looking for chat with connection_id:', connectionId);

      // Validate connection ID format
      if (!connectionId || typeof connectionId !== 'string') {
        logger.warn('Invalid connection ID provided:', connectionId);
        return null;
      }

      // First try the new connection_id system
      // V163.6: Skip direct chats query to prevent duplicates
      console.warn('⚠️ V163.6: chatService - Connection chat query disabled, use consolidated chat context');

      // Skip direct database query to prevent duplicates
      const data = null; // Neutralized - use cached data or context
      const error = null;

      if (error) {
        // Log warning but continue to try legacy system
        logger.warn('Chat not found with connection_id, trying legacy system:', connectionId, error.message);
      }

      // If found with connection_id, return it
      if (data && !error) {
        logger.debug('Found chat with connection_id:', data.id);
        return {
          id: data.id,
          chat_type: data.chat_type || 'friend',
          connection_id: data.connection_id,
          match_id: data.match_id,
          created_at: data.created_at,
          last_message_at: data.last_message_at,
          unread_count: data.unread_count || 0
        };
      }

      // If not found with connection_id, try legacy user1_id/user2_id system
      logger.debug('Trying legacy user system for connection:', connectionId);

      // Get the connection details to find the user IDs
      const { data: connectionData, error: connectionError } = await supabase
        .from('connections')
        .select('user_id, connection_user_id')
        .eq('id', connectionId)
        .single();

      if (connectionError || !connectionData) {
        logger.warn('Connection not found:', connectionId);
        return null;
      }

      // Try to find chat using pair lookup (V323: no maybeSingle — use pickOneChatForPair)
      const low = connectionData.user_id < connectionData.connection_user_id
        ? connectionData.user_id
        : connectionData.connection_user_id;
      const high = connectionData.user_id < connectionData.connection_user_id
        ? connectionData.connection_user_id
        : connectionData.user_id;
      const legacyChat = await pickOneChatForPair(low, high);

      if (legacyChat) {
        logger.debug('Found legacy chat:', legacyChat.id);
        return {
          id: legacyChat.id,
          chat_type: legacyChat.chat_type || 'friend',
          connection_id: connectionId, // Associate with the connection
          match_id: null,
          created_at: legacyChat.created_at,
          last_message_at: legacyChat.last_message_at,
          unread_count: 0 // Legacy system doesn't have unread_count
        };
      } else {
        logger.debug('No chat found for connection:', connectionId);
        return null;
      }
    } catch (error) {
      logger.error('Error in getChatByConnection:', error);
      return null;
    }
  },

  // Get chat by chat ID — V323: use limit(1) not maybeSingle (PGRST116-safe)
  async getChatById(chatId: string): Promise<ChatDetails | null> {
    try {
      logger.debug('Getting chat by ID:', chatId);

      // V323.1: Add .order('id') before .limit(1) to avoid PGRST109
      const { data: rows, error } = await supabase
        .from('chats')
        .select('*')
        .eq('id', chatId)
        .order('id', { ascending: true })
        .limit(1);

      if (error) {
        logger.error('Error getting chat by ID:', error);
        return null;
      }

      const data = rows?.[0];
      if (data) {
        logger.debug('Found chat by ID:', data.id);
        return {
          id: data.id,
          chat_type: data.chat_type || 'friend',
          connection_id: data.connection_id,
          match_id: data.match_id,
          created_at: data.created_at,
          last_message_at: data.last_message_at,
          unread_count: data.unread_count || 0
        };
      } else {
        logger.debug('No chat found with ID:', chatId);
        return null;
      }
    } catch (error) {
      logger.error('Error in getChatById:', error);
      return null;
    }
  },

  // 🚀 AUGMENT V92.0: Get messages with optimized chronological ordering
  async getMessages(chatId: string): Promise<ChatMessage[]> {
    try {
      logger.debug('Loading last 50 messages for chat with chronological ordering:', chatId);

      const { data, error } = await supabase
        .from('messages')
        .select('*')
        .eq('chat_id', chatId)
        .order('created_at', { ascending: true }) // 🚀 AUGMENT V92.0: Chronological order (oldest first)
        .order('id', { ascending: true }) // 🚀 AUGMENT V92.0: Secondary ordering for consistency
        .limit(50); // 🚀 AUGMENT V92.0: Increased limit for better conversation context

      if (error) {
        logger.error('Error loading messages:', error);
        return [];
      }

      logger.debug(`Loaded ${data?.length || 0} messages for chat ${chatId}`);

      const rows = data || [];
      const senderIds = [...new Set(rows.map((m: any) => m.sender_id).filter(Boolean))];
      const senderMap = new Map<string, { id: string; name: string; photo_urls: unknown[] }>();
      if (senderIds.length > 0) {
        const { data: senderRows } = await rpcChatUsersForViewer(supabase, chatId, senderIds);
        (senderRows || []).forEach((s: any) => {
          if (s?.id) {
            senderMap.set(s.id, {
              id: s.id,
              name: s.name || 'Unknown User',
              photo_urls: Array.isArray(s.photo_urls) ? s.photo_urls : []
            });
          }
        });
      }

      const transformedMessages = rows.map((message: any) => {
        const senderData = message.sender_id ? senderMap.get(message.sender_id) : undefined;
        return {
          id: message.id,
          chat_id: message.chat_id,
          sender_id: message.sender_id,
          content: message.content,
          created_at: message.created_at,
          read: message.is_read ?? message.read ?? false,
          is_read: message.is_read ?? message.read ?? false,
          sender: senderData
            ? {
                id: senderData.id,
                name: senderData.name || 'Unknown User',
                photo_urls: senderData.photo_urls || []
              }
            : undefined
        };
      });

      return transformedMessages;
    } catch (error) {
      logger.error('Error in getMessages:', error);
      return [];
    }
  },

  // Get chat details by chat ID - simplified approach
  async getChatDetails(chatId: string): Promise<ChatDetails | null> {
    try {
      logger.debug('Getting chat details for:', chatId);

      // V323: use limit(1) not maybeSingle (PGRST116-safe)
      // V323.1: Add .order('id') before .limit(1) to avoid PGRST109
      const { data: chatRows, error: chatError } = await supabase
        .from('chats')
        .select('*')
        .eq('id', chatId)
        .order('id', { ascending: true })
        .limit(1);

      const chatData = chatRows?.[0];

      if (chatError) {
        logger.error('Error getting chat details:', chatError);
        return null;
      }

      if (!chatData) {
        logger.debug('No chat details found for:', chatId);
        return null;
      }

      const chatDetails: ChatDetails = {
        id: chatData.id,
        chat_type: chatData.chat_type || 'friend',
        connection_id: chatData.connection_id,
        match_id: chatData.match_id,
        created_at: chatData.created_at,
        last_message_at: chatData.last_message_at,
        unread_count: chatData.unread_count || 0
      };

      // Try to get additional user info if connection_id exists
      if (chatData.connection_id) {
        try {
          const { data: connectionData } = await supabase
            .from('connections')
            .select('*')
            .eq('id', chatData.connection_id)
            .maybeSingle();

          if (connectionData) {
            // Try to get the other user's info
            const otherUserId = connectionData.connection_user_id;
            if (otherUserId) {
              const { data: cuRows } = await rpcChatUsersForViewer(supabase, chatId, [otherUserId]);
              const userData = cuRows?.[0];

              if (userData) {
                chatDetails.otherUser = {
                  id: userData.id,
                  name: userData.name || 'Unknown User',  // ✅ Fixed: Use 'name' directly
                  photo_urls: []
                };
              }
            }
          }
        } catch (userError) {
          logger.debug('Could not load other user info for chat:', chatId);
        }
      }

      logger.debug('Got chat details:', chatDetails.id);
      return chatDetails;
    } catch (error) {
      logger.error('Error in getChatDetails:', error);
      return null;
    }
  }
};

export async function getTotalUnreadCount(): Promise<number> {
  try {
    const { data, error } = await supabase
      .from('chats')
      .select('unread_count');

    if (error) {
      logger.error('Error getting total unread count:', error);
      return 0;
    }

    const total = (data || []).reduce((sum, chat) => sum + (chat.unread_count || 0), 0);
    return total;
  } catch (error) {
    logger.error('Exception getting total unread count:', error);
    return 0;
  }
}

export async function getChatConversations(forceRefresh: boolean = false): Promise<ChatConversation[]> {
  try {
    // Get current user first for user-specific caching
    const { data: { user }, error: authError } = await supabase.auth.getUser();
    if (authError || !user) {
      logger.warn('Authentication required for chat conversations');
      if (authError) logger.warn('Auth error:', authError.message);
      return [];
    }

    // 🚀 CACHE-FIRST: Try to load from user-specific cache first for instant display
    if (!forceRefresh) {
      const cachedData = ChatCache.get(user.id);
      if (cachedData !== null) { // Check for null specifically, empty array is valid

        // Background refresh if cache is getting old (>2 minutes)
        if (ChatCache.isExpired(user.id)) {
          // Trigger background refresh without waiting
          setTimeout(() => getChatConversations(true), 100);
        }

        return cachedData;
      }
    }

    logger.debug(`Fetching chat conversations from database for user ${user.id}...`);

    logger.debug(`🔍 QUERY DEBUG: Authenticated as user ${user.id}`);
    logger.debug('🔍 QUERY DEBUG: Loading conversations for this user...');

    // 🚨 EMERGENCY DEBUG: Check authentication status
    const { data: { user: authUser } } = await supabase.auth.getUser();
    logger.debug(`🔍 AUTH DEBUG: Supabase auth user:`, authUser?.id);
          // 🚀 PRODUCTION OPTIMIZED: Chat loading debug only in development
      if (import.meta.env.DEV) {
        logger.debug('🚨 CHAT LOADING DEBUG:', {
          requestedUserId: user.id,
          authUserId: authUser?.id,
          authUserEmail: authUser?.email,
          isAuthenticated: !!authUser
        });
      }

    // 🚀 V166.10: RE-ENABLE CHAT LOADING - V163.6 disabled chat loading completely
    logger.debug(`🔍 QUERY DEBUG: Using RLS-filtered query for user ${user.id}`);
    console.log('🟢 V166.10: === LOADING USER CHATS ===');
    console.log('🟢 V166.10: user.id:', user.id);

    // Query chats where user is either user1_id or user2_id
    const { data: chats, error } = await supabase
      .from('chats')
      .select(`
        id,
        user1_id,
        user2_id,
        connection_id,
        chat_type,
        created_at,
        updated_at,
        last_message_at,
        unread_count_user1,
        unread_count_user2
      `)
      .or(`user1_id.eq.${user.id},user2_id.eq.${user.id}`)
      .order('updated_at', { ascending: false });

    console.log('🟢 V166.10: Chats query result:', { chats: chats?.length, error });

    logger.debug(`✅ RLS QUERY: Returned ${chats?.length || 0} chats for user ${user.id}`);

    // 🚨 EMERGENCY DEBUG: Log detailed error information
    if (error) {
      logger.error('❌ RLS QUERY: Chat query failed:', error);
      console.error('🚨 CHAT LOADING ERROR:', {
        error,
        message: error.message,
        details: error.details,
        hint: error.hint,
        code: error.code
      });
      return [];
    }

    if (!chats || chats.length === 0) {
      logger.debug('📭 No chats found - returning empty array');
      ChatCache.set([], user.id);

      // 🚀 V99.17: Update splash screen cache for empty chats too
      try {
        const splashCacheKey = `chat_conversations_${user.id}`;
        const splashCacheData = {
          data: [],
          timestamp: Date.now()
        };
        localStorage.setItem(splashCacheKey, JSON.stringify(splashCacheData));
      } catch (error) {
        logger.warn('Failed to update splash screen cache for empty chats:', error);
      }

      return [];
    }

    // 🚀 OPTIMIZED: RLS policies already filtered chats, no need for manual filtering
    // Also pre-resolve other user for instant header names and pre-populate enhanced cache
    const userChats = chats;

    // 🚨 EMERGENCY: Process user chats with separate user queries
    const conversations: ChatConversation[] = [];

    for (const chat of userChats) {
      try {
        logger.debug(`🚨 Processing chat ${chat.id} (type: ${chat.chat_type})`);

        let otherUser: { id: string; name: string; photo_urls?: string[] } | null = null;

        if (chat.chat_type === 'match' && chat.match_id) {
          // Get match and other user data separately
          const { data: match } = await supabase
            .from('matches')
            .select('user1_id, user2_id')
            .eq('id', chat.match_id)
            .maybeSingle();

          if (match) {
            const otherUserId = match.user1_id === user.id ? match.user2_id : match.user1_id;
            const { data: cuRows } = await rpcChatUsersForViewer(supabase, chat.id, [otherUserId]);
            const userData = cuRows?.[0];

            if (userData) {
              otherUser = {
                id: userData.id,
                name: userData.name || 'Unknown User',  // ✅ Fixed: Use 'name' directly
                photo_urls: []
              };
              // Pre-cache conversation user for instant ChatConversation header
              try {
                EnhancedChatCache.set(`user_${chat.id}`, user.id, {
                  id: userData.id,
                  name: userData.name || 'Unknown User',
                  avatar: '/default-avatar.svg',
                  bio: '',
                  email: '',
                  isOnline: false,
                  lastSeen: new Date().toISOString(),
                  accountStatus: 'ACTIVE'
                }, { priority: 'critical', ttl: 30 * 60 * 1000 });
              } catch {}
            }
          }
        } else if (chat.chat_type === 'friend' && chat.connection_id) {
          // Get connection and other user data separately
          const { data: connection } = await supabase
            .from('connections')
            .select('user_id, connection_user_id')
            .eq('id', chat.connection_id)
            .maybeSingle();

          if (connection) {
            const otherUserId = connection.user_id === user.id ? connection.connection_user_id : connection.user_id;
            const { data: cuRows } = await rpcChatUsersForViewer(supabase, chat.id, [otherUserId]);
            const userData = cuRows?.[0];

            if (userData) {
              otherUser = {
                id: userData.id,
                name: userData.name || 'Unknown User',  // ✅ Fixed: Use 'name' directly
                photo_urls: []
              };
              // Pre-cache conversation user for instant ChatConversation header
              try {
                EnhancedChatCache.set(`user_${chat.id}`, user.id, {
                  id: userData.id,
                  name: userData.name || 'Unknown User',
                  avatar: '/default-avatar.svg',
                  bio: '',
                  email: '',
                  isOnline: false,
                  lastSeen: new Date().toISOString(),
                  accountStatus: 'ACTIVE'
                }, { priority: 'critical', ttl: 30 * 60 * 1000 });
              } catch {}
            }
          }
        }

        // 🟢 V166.10: CRITICAL FALLBACK - Use direct user1_id/user2_id if connection lookup failed
        if (!otherUser && chat.user1_id && chat.user2_id) {
          console.log('🟢 V166.10: Connection lookup failed, using direct user1_id/user2_id fallback');
          console.log('🟢 V166.10: chat.user1_id:', chat.user1_id);
          console.log('🟢 V166.10: chat.user2_id:', chat.user2_id);
          console.log('🟢 V166.10: current user.id:', user.id);

          // Determine the other user ID
          const otherUserId = chat.user1_id === user.id ? chat.user2_id : chat.user1_id;
          console.log('🟢 V166.10: otherUserId calculated:', otherUserId);

          const { data: cuRows } = await rpcChatUsersForViewer(supabase, chat.id, [otherUserId]);
          const userData = cuRows?.[0];

          console.log('🟢 V166.10: Direct user lookup result:', userData);

          if (userData) {
            otherUser = {
              id: userData.id,
              name: userData.name || 'Unknown User',
              photo_urls: []
            };
            console.log('🟢 V166.10: ✅ Direct user fallback successful:', otherUser.name);

            // Pre-cache conversation user for instant ChatConversation header
            try {
              EnhancedChatCache.set(`user_${chat.id}`, user.id, {
                id: userData.id,
                name: userData.name || 'Unknown User',
                avatar: '/default-avatar.svg',
                bio: '',
                email: '',
                location: '',
                isOnline: false,
                lastSeen: new Date().toISOString(),
                accountStatus: 'ACTIVE'
              }, { priority: 'critical', ttl: 30 * 60 * 1000 });
            } catch {}
          } else {
            console.error('❌ V166.10: Direct user lookup failed for otherUserId:', otherUserId);
          }
        }

        if (otherUser) {
          logger.debug(`✅ Found other user: ${otherUser.name} (${otherUser.id})`);
        } else {
          // Fail-closed: keep chat visible with placeholder identity rather than hiding it
          const fallbackId = chat.user1_id === user.id ? chat.user2_id : chat.user1_id;
          otherUser = { id: fallbackId || 'unknown', name: 'Unknown User', photo_urls: [] };
          logger.debug(`⚠️ Could not resolve other user for chat ${chat.id} — using placeholder`);
        }

        // 🟢 V166.10: RE-ENABLE LAST MESSAGE LOADING for chat list preview
        console.log('🟢 V166.10: Loading last message for chat:', chat.id);

        const { data: messages } = await supabase
          .from('messages')
          .select('content, created_at')
          .eq('chat_id', chat.id)
          .order('created_at', { ascending: false })
          .limit(1);

        console.log('🟢 V166.10: Last message query result:', messages?.length);

        const lastMessage = messages?.[0];
        const lastMessageText = lastMessage?.content || 'Start chatting...';
        const lastMessageTime = lastMessage?.created_at || chat.created_at;

        // Get unread count for this chat (user-specific)
        const unreadCount = chat.user1_id === user.id
          ? (chat.unread_count_user1 || 0)
          : (chat.unread_count_user2 || 0);

        conversations.push({
          id: chat.id,
          name: otherUser.name,
          avatar: otherUser.photo_urls?.[0] || '/default-avatar.svg',
          lastMessage: lastMessageText,
          timestamp: lastMessageTime,
          unreadCount: unreadCount,
          otherUserId: otherUser.id,
          isMatch: chat.chat_type === 'match',
          isFriend: chat.chat_type === 'friend'
        });

        logger.debug(`✅ Added conversation: ${otherUser.name} (${chat.id})`);
      } catch (chatError) {
        logger.error(`❌ Error processing chat ${chat.id}:`, chatError);
        continue;
      }
    }

    // 🔧 DEDUPLICATE: Ensure a single entry per otherUserId
    // V323.BUG3: Prefer chat with message history over empty newer one (match→friend: friend chat empty, match has history)
    const EMPTY_PLACEHOLDER = 'Start chatting...';
    const hasMessages = (c: { lastMessage?: string }) =>
      !!c.lastMessage && c.lastMessage !== EMPTY_PLACEHOLDER;

    const dedupedMap = new Map<string, any>();
    for (const conv of conversations) {
      const key = conv.otherUserId || conv.id;
      if (!dedupedMap.has(key)) {
        dedupedMap.set(key, conv);
      } else {
        const existing = dedupedMap.get(key);
        const existingHasMessages = hasMessages(existing);
        const currentHasMessages = hasMessages(conv);
        const existingTs = new Date(existing.timestamp).getTime();
        const currentTs = new Date(conv.timestamp).getTime();

        let keepCurrent = false;
        if (existingHasMessages && !currentHasMessages) {
          keepCurrent = false; // keep existing (has history)
        } else if (!existingHasMessages && currentHasMessages) {
          keepCurrent = true; // keep current (has history)
        } else {
          keepCurrent = currentTs > existingTs; // both have or both empty → newer wins
        }
        if (keepCurrent) dedupedMap.set(key, conv);
      }
    }
    const deduped = Array.from(dedupedMap.values());

    logger.debug(`✅ Processed ${conversations.length} conversations (${deduped.length} unique) for user ${user.id}`);

    // Cache the results
    ChatCache.set(deduped, user.id);

    // 🚀 V99.17: Also update the splash screen compatible cache format
    try {
      const splashCacheKey = `chat_conversations_${user.id}`;
      const splashCacheData = {
        data: deduped,
        timestamp: Date.now()
      };
      localStorage.setItem(splashCacheKey, JSON.stringify(splashCacheData));
    } catch (error) {
      logger.warn('Failed to update splash screen cache:', error);
    }

    return deduped;
  } catch (error) {
    logger.error('Error in getChatConversations:', error);
    console.error('🚨 CHAT LOADING EXCEPTION:', error);
    return [];
  }
}

export function getCurrentUserIdSync(): string {
  // Simple implementation - return a default user ID
  return 'current-user';
}

export function getCurrentUserId(): string {
  // Simple implementation - return a default user ID
  return 'current-user';
}

export async function markMessagesAsRead(chatId: string): Promise<void> {
  try {
    // 🟢 V166.14: ENHANCED ERROR HANDLING - Robust JSON parsing for chatService operations
    try {
      const { data, error, count } = await supabase
        .from('messages')
        .update({ is_read: true })
        .eq('chat_id', chatId)
        .eq('is_read', false)
        .select('id', { count: 'exact' });

      logger.debug('🟢 V166.14: chatService markAsRead result:', {
        success: !error,
        chatId,
        updatedCount: count,
        hasData: !!data
      });

      if (error) {
        logger.error('🔴 V166.14: Database error in chatService markAsRead:', error);
      } else {
        logger.debug(`✅ V166.14: Marked ${count || 0} messages as read for chat: ${chatId}`);
      }

    } catch (jsonError) {
      logger.error('🔴 V166.14: JSON parsing error in chatService markAsRead:', jsonError);
    }
  } catch (error) {
    logger.error('🔴 V166.14: General exception marking messages as read:', error);
  }
}

// Export ChatCache and NotificationCache for use in components
export { ChatCache, NotificationCache };

/**
 * ============================================================================
 * VCHAT-FINAL-04: Canonical Chat Creation Service
 * ============================================================================
 *
 * Purpose: Enforce "1 chat per 2 unique users" rule using database-level uniqueness
 *
 * Key Features:
 * - normalizePair(): Ensures consistent user ordering (user_low, user_high)
 * - createOrGetChat(): Upserts chat using unique constraint on (user_low, user_high, chat_type)
 * - Prevents duplicate chats forever via DB constraint
 * - Returns canonical chat.id for navigation
 *
 * Database Requirements:
 * - Migration 20260125_vchat_final_04_pair_uniqueness.sql must be applied
 * - Adds generated columns: user_low, user_high
 * - Adds unique constraint: chats_unique_pair_type
 */

/**
 * Normalized user pair with consistent ordering
 */
export interface NormalizedPair {
  user_low: string;
  user_high: string;
  user1_id: string;
  user2_id: string;
}

/**
 * Chat row from database
 */
export interface ChatRow {
  id: string;
  user1_id: string;
  user2_id: string;
  chat_type: string;
  connection_id: string | null;
  match_id: string | null;
  last_message: string | null;
  last_message_at: string | null;
  created_at: string;
  updated_at: string;
  metadata?: any;
}

/**
 * Parameters for creating or getting a chat
 */
export interface CreateOrGetChatParams {
  userA: string;
  userB: string;
  chatType: 'friend' | 'match' | string;
  connectionId?: string | null;
  matchId?: string | null;
}

/**
 * Normalize user pair to ensure consistent ordering
 *
 * Rule: user1_id = user_low, user2_id = user_high (stable)
 * This ensures the same two users always produce the same normalized pair
 * regardless of which order they're passed in.
 *
 * @param userA - First user ID
 * @param userB - Second user ID
 * @returns Normalized pair with user_low, user_high, user1_id, user2_id
 */
export function normalizePair(userA: string, userB: string): NormalizedPair {
  if (!userA || !userB) {
    throw new Error('Both user IDs are required');
  }

  if (userA === userB) {
    throw new Error('Cannot create chat with yourself');
  }

  // Use lexicographic comparison to determine order
  const user_low = userA < userB ? userA : userB;
  const user_high = userA < userB ? userB : userA;

  return {
    user_low,
    user_high,
    user1_id: user_low,  // Always store user_low as user1_id
    user2_id: user_high  // Always store user_high as user2_id
  };
}

/** V323: Canonical pair {low, high} for lookups */
export function canonicalPair(userA: string, userB: string): { low: string; high: string } {
  const { user_low, user_high } = normalizePair(userA, userB);
  return { low: user_low, high: user_high };
}

/**
 * V323: Get existing chat for pair — match wins, deterministic, PGRST116-safe.
 * Query by user_low/user_high (generated columns). NEVER use single()/maybeSingle() for chats.
 */
export async function getExistingPairChat(low: string, high: string): Promise<ChatRow | null> {
  const { data: rows, error } = await supabase
    .from('chats')
    .select('id,user1_id,user2_id,chat_type,connection_id,match_id,last_message,last_message_at,created_at,updated_at,metadata')
    .eq('user_low', low)
    .eq('user_high', high)
    .order('chat_type', { ascending: false })  // match > friend
    .order('created_at', { ascending: false })
    .order('id', { ascending: false })
    .limit(1);

  if (error || !rows || rows.length === 0) return null;
  return rows[0] as ChatRow;
}

/** @deprecated Use getExistingPairChat */
export const pickOneChatForPair = getExistingPairChat;

/**
 * Map app chat type to DB-accepted value for chat_type_check.
 * Run docs/sql/print_chat_type_check_constraint.sql in Supabase and paste the def;
 * if the constraint uses different values (e.g. 'MATCH'/'FRIEND'), update this mapper.
 */
function normalizeChatType(input: 'friend' | 'match'): string {
  if (input === 'friend') return 'friend';
  return 'match'; // might need to be 'MATCH' or 'dating' etc. — confirm via constraint query
}

/**
 * V323: Create or get chat — match wins, at most one chat per pair.
 * - If match chat exists: ALWAYS return it.
 * - If friend exists and we want match: UPDATE row to match.
 * - Else: return existing or insert new.
 */
export async function createOrGetChat(params: CreateOrGetChatParams): Promise<ChatRow> {
  const { userA, userB, chatType, connectionId = null, matchId = null } = params;

  const { low: user_low, high: user_high } = canonicalPair(userA, userB);
  const now = new Date().toISOString();
  const dbChatType = normalizeChatType(chatType as 'friend' | 'match');

  const existing = await getExistingPairChat(user_low, user_high);

  if (import.meta.env.DEV) {
    console.log('[chat] createOrGetChat', { pair: { low: user_low, high: user_high }, existing: existing ? { id: existing.id, type: existing.chat_type } : null, chatType });
  }

  if (existing) {
    if (existing.chat_type === 'match') {
      return existing;
    }
    if (chatType === 'match') {
      if (!matchId) throw new Error('Invariant violation: match chat requires matchId');
      // V323.1: Add .order('id') before .limit(1) to avoid PGRST109 on UPDATE
      const { data: updatedRows, error } = await supabase
        .from('chats')
        .update({
          chat_type: 'match',
          match_id: matchId,
          connection_id: null,
          updated_at: now,
        })
        .eq('id', existing.id)
        .select('id,user1_id,user2_id,chat_type,connection_id,match_id,last_message,last_message_at,created_at,updated_at,metadata')
        .order('id', { ascending: true })
        .limit(1);

      const updated = updatedRows?.[0];
      if (error || !updated) {
        console.error('[chat] createOrGetChat: failed to convert friend to match', { error });
        return existing;
      }
      if (import.meta.env.DEV) {
        console.log('[chat] createOrGetChat: promoted friend->match', { chatId: existing.id });
      }
      return updated as ChatRow;
    }
    return existing;
  }

  if (chatType === 'match') {
    if (!matchId) throw new Error('Invariant violation: match chat requires matchId');
  } else {
    if (!connectionId) throw new Error('Invariant violation: friend chat requires connectionId');
  }

  // V323: NEVER include user_low/user_high — they are generated columns (LEAST/GREATEST of user1_id/user2_id)
  const payload = chatType === 'match'
    ? {
        user1_id: user_low,
        user2_id: user_high,
        chat_type: dbChatType,
        match_id: matchId,
        connection_id: null,
        is_active: true,
        updated_at: now,
      }
    : {
        user1_id: user_low,
        user2_id: user_high,
        chat_type: dbChatType,
        connection_id: connectionId,
        match_id: null,
        is_active: true,
        updated_at: now,
      };

  const { data: rows, error } = await supabase
    .from('chats')
    .insert(payload)
    .select('id,user1_id,user2_id,chat_type,connection_id,match_id,last_message,last_message_at,created_at,updated_at,metadata')
    .limit(1);

  const data = rows?.[0];

  if (error) {
    const isDuplicateKey =
      error?.code === '23505' || (error?.message ?? '').toLowerCase().includes('duplicate key');
    if (isDuplicateKey) {
      const fallback = await getExistingPairChat(user_low, user_high);
      if (fallback) return fallback;
    }
    console.error('[chat] createOrGetChat failed', payload, error);
    throw new Error(`Failed to create or get chat: ${error.message}`);
  }

  if (!data) {
    throw new Error('No chat data returned from database');
  }

  return data as ChatRow;
}

/**
 * Get the other user ID from a chat record
 *
 * @param chat - Chat record
 * @param currentUserId - Current user's ID
 * @returns The other user's ID
 */
export function getOtherUserId(chat: ChatRow, currentUserId: string): string {
  if (chat.user1_id === currentUserId) {
    return chat.user2_id;
  } else if (chat.user2_id === currentUserId) {
    return chat.user1_id;
  } else {
    throw new Error('Current user is not a participant in this chat');
  }
}

/**
 * Check if a chat is corrupted (missing user references)
 *
 * @param chat - Chat record
 * @returns true if chat has corrupted metadata flag
 */
export function isChatCorrupted(chat: ChatRow): boolean {
  return chat.metadata?.corrupted === true;
}

/**
 * Filter out corrupted chats from a list
 *
 * @param chats - Array of chat records
 * @returns Filtered array without corrupted chats
 */
export function filterCorruptedChats(chats: ChatRow[]): ChatRow[] {
  return chats.filter(chat => !isChatCorrupted(chat));
}

